export type TranslateFn = (key: string, options?: Record<string, unknown>) => string

interface DurationFormatLike {
  format(duration: { minutes?: number; seconds?: number }): string
}

interface DurationFormatCtor {
  new (locale: string, options?: { style?: 'long' | 'short' | 'narrow' }): DurationFormatLike
}

/** `Intl.DurationFormat` isn't in every runtime yet (and isn't declared by
 * TypeScript's own `lib.es*.d.ts` at the time of writing), so this is a type
 * guard rather than a cast — it narrows `typeof Intl` only when the
 * constructor is actually present. */
function hasDurationFormat(intl: typeof Intl): intl is typeof Intl & {
  DurationFormat: DurationFormatCtor
} {
  return 'DurationFormat' in intl
}

/**
 * Formats a duration (seconds) as a spoken string for `aria-valuetext` —
 * "1 min 12 s" via `Intl.DurationFormat` where available, falling back to
 * the `read_mode.listen.minutes_seconds` translation key otherwise.
 */
export function formatSpokenDuration(
  totalSecondsValue: number,
  locale: string,
  translate: TranslateFn,
): string {
  const safeSeconds = Math.max(0, Math.round(totalSecondsValue))
  const minutes = Math.floor(safeSeconds / 60)
  const seconds = safeSeconds % 60

  const intl = Intl
  if (hasDurationFormat(intl)) {
    const formatter = new intl.DurationFormat(locale, { style: 'short' })
    return formatter.format({ minutes, seconds })
  }

  return translate('read_mode.listen.minutes_seconds', { m: minutes, s: seconds })
}
